export interface ParsedAddress {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

/**
 * Adds periods after common street and unit abbreviations
 */
function addPeriodsToAbbreviations(address: string): string {
  if (!address || typeof address !== 'string') {
    return address;
  }

  // Common street abbreviations that should have periods
  const streetAbbreviations = [
    'Rd', 'Dr', 'St', 'Ave', 'Pl', 'Ln', 'Terr', 'Ct', 'Cir', 'Blvd', 
    'Pkwy', 'Hwy', 'Expy', 'Fwy', 'Rte', 'Trl', 'Way', 'Sq', 'Loop',
    'Apt', 'Ste', 'Unit', 'Bldg', 'Flr', 'Rm'
  ];

  let result = address;
  
  streetAbbreviations.forEach(abbrev => {
    // Add period after abbreviation if it doesn't already have one
    // Uses word boundaries to avoid partial matches
    const regex = new RegExp(`\\b${abbrev}\\b(?!\\.)`);
    result = result.replace(regex, `${abbrev}.`);
  });

  return result;
}

/**
 * Parses a Google Maps address string into constituent address fields
 * 
 * Examples:
 * - "2912B Jones Ferry Rd, Chapel Hill, NC 27516" 
 *   -> { address1: "2912B Jones Ferry Rd.", address2: "", city: "Chapel Hill", state: "NC", zip: "27516", country: "USA" }
 * - "3201 Northside Dr #119, Raleigh, NC 27615"
 *   -> { address1: "3201 Northside Dr. #119", address2: "", city: "Raleigh", state: "NC", zip: "27615", country: "USA" }
 */
export function parseGoogleMapsAddress(addressString: string): ParsedAddress {
  // Initialize with defaults
  const result: ParsedAddress = {
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "USA"
  };

  if (!addressString || typeof addressString !== 'string') {
    return result;
  }

  // Clean the input
  const cleanAddress = addressString.trim();
  
  // Split by commas
  const parts = cleanAddress.split(',').map(part => part.trim());
  
  if (parts.length >= 3) {
    // Expected format: "Street Address, City, State ZIP"
    
    // First part is street address - add periods to abbreviations
    result.address1 = addPeriodsToAbbreviations(parts[0]);
    
    // Second part is city
    result.city = parts[1];
    
    // Third part contains state and ZIP
    const stateZipPart = parts[2];
    
    // Extract state and ZIP using regex
    // Matches patterns like "NC 27516", "CA 90210", etc.
    const stateZipMatch = stateZipPart.match(/^([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    
    if (stateZipMatch) {
      result.state = stateZipMatch[1];
      result.zip = stateZipMatch[2];
    } else {
      // Fallback: try to extract just the state if ZIP is missing
      const stateMatch = stateZipPart.match(/^([A-Z]{2})/);
      if (stateMatch) {
        result.state = stateMatch[1];
        // Try to find ZIP elsewhere in the string
        const zipMatch = stateZipPart.match(/(\d{5}(?:-\d{4})?)/);
        if (zipMatch) {
          result.zip = zipMatch[1];
        }
      }
    }
  } else if (parts.length === 2) {
    // Handle cases with only 2 parts
    result.address1 = addPeriodsToAbbreviations(parts[0]);
    
    // Try to parse the second part as "City, State ZIP"
    const secondPart = parts[1];
    const cityStateZipMatch = secondPart.match(/^(.+?)\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    
    if (cityStateZipMatch) {
      result.city = cityStateZipMatch[1];
      result.state = cityStateZipMatch[2];
      result.zip = cityStateZipMatch[3];
    }
  } else if (parts.length === 1) {
    // Single part - try to extract what we can
    const singlePart = parts[0];
    
    // Check if it contains state and ZIP at the end
    const fullMatch = singlePart.match(/^(.+?),?\s+(.+?),?\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    
    if (fullMatch) {
      result.address1 = addPeriodsToAbbreviations(fullMatch[1]);
      result.city = fullMatch[2];
      result.state = fullMatch[3];
      result.zip = fullMatch[4];
    } else {
      // Just put everything in address1 with periods added
      result.address1 = addPeriodsToAbbreviations(singlePart);
    }
  }

  return result;
}

/**
 * Checks if an address string appears to be a valid Google Maps format
 */
export function isValidGoogleMapsAddress(addressString: string): boolean {
  if (!addressString || typeof addressString !== 'string') {
    return false;
  }

  const cleanAddress = addressString.trim();
  
  // Check for basic patterns that indicate a Google Maps address
  // Should have at least street, city, and state
  const basicPattern = /.*,\s*.*,\s*[A-Z]{2}/;
  
  return basicPattern.test(cleanAddress);
} 